import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AttachmentModal } from '../attachments/AttachmentModal'
import { useGallery } from '../attachments/gallery'
import type { PreviewItem } from '../attachments/types'
import type { ToolCardData } from '../lib/segments'
import { CopyButton } from './CopyButton'
import { DownloadIcon } from './icons'

/**
 * Bir arac cagrisinin karti.
 *
 * Vanilla arayuz bunu elle HTML string birlestirerek kuruyordu ve icinde
 * `b64()`/`atob`/`unescape` gidis-donusu vardi (kopyalanacak kodu HTML
 * attribute'una gomebilmek icin). React'te o gidis-donus TAMAMEN yok oluyor:
 * `<CopyButton text={code}/>` string'i closure ile kapatiyor.
 *
 * Kart icerigi markdown/sanitize hattina HIC girmiyor - kod ve cikti duz
 * metin olarak `<pre>` icine yaziliyor, yani React'in kendi kacislari
 * yeterli.
 */
export function ToolCard({ data }: { data: ToolCardData }) {
  if (data.kind === 'code') return <CodeCard data={data} />
  if (data.kind === 'search') return <SearchCard data={data} />
  if (data.kind === 'fetch') return <FetchCard data={data} />
  if (data.kind === 'memory') return <MemoryCard data={data} />
  if (data.kind === 'recall') return <RecallCard data={data} />
  if (data.kind === 'convert') return <ConvertCard data={data} />
  if (data.kind === 'image') return <ImageCard data={data} />
  if (data.kind === 'local') return <LocalCard data={data} />
  return <UnknownCard kind={data.kind} />
}

/**
 * Sunucudaki `generate_image` / `edit_image` araclari. Uretilen gorsel asistan
 * mesajina ek olarak bagli; kart onizlemeyi (tiklayinca tam boy) ve indirme
 * baglantisini gosteriyor. Istem modelin Ingilizce tarifi - kopyalanabilir.
 */
function ImageCard({ data }: { data: ToolCardData }) {
  const { t } = useTranslation()
  const prompt = asText(data.prompt)
  const output = asText(data.output)
  const files = asOutputFiles(data.output_attachments)
  const check = asImageCheck(data.check)
  // Hangi gorsel duzenlendi: gozlenen hata - model dosya adini kisaltinca ESKI bir surum
  // sessizce duzenlendi ve kullanici bunu fark edemedi.
  const source = data.operation === 'edit' ? asText(data.source) : ''
  const label = data.operation === 'edit' ? t('tool.image.edit') : t('tool.image.label')
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const gallery = useGallery()
  // Kullanicinin yukledigi eklerle AYNI onizleme modali. Onceden gorsel
  // `/api/attachments/{id}` ham adresine giden bir baglantiydi ve tiklayinca
  // tarayici sekmesinde ciplak dosya aciliyordu (kullanici geri bildirimi).
  const previews = useMemo<PreviewItem[]>(
    () =>
      files.map((file) => ({
        kind: 'image',
        filename: file.filename,
        mime: file.mime ?? 'image/png',
        byteSize: file.byte_size ?? 0,
        previewUrl: file.url,
        downloadUrl: file.url,
        width: file.width ?? null,
        height: file.height ?? null,
      })),
    [files],
  )
  return (
    <section className="toolcard" aria-label={label}>
      <header>
        <span className="toolcard-kind">{label}</span>
        {prompt && <CopyButton text={prompt} iconOnly />}
      </header>
      {files.map((file, index) => (
        <button
          key={file.id}
          type="button"
          className="toolcard-image"
          onClick={() => {
            if (!gallery?.openById(file.id)) setOpenIndex(index)
          }}
          aria-label={t('attach.preview.label', { name: file.filename })}
        >
          <img
            src={file.url}
            alt={prompt || file.filename}
            width={file.width ?? undefined}
            height={file.height ?? undefined}
            loading="lazy"
          />
        </button>
      ))}
      {openIndex !== null && (
        <AttachmentModal
          items={previews}
          startIndex={openIndex}
          onClose={() => setOpenIndex(null)}
        />
      )}
      {/*
        Sunucudaki oz-denetim: model uretilen gorseli gorup istege uygunlugunu
        kontrol etti. Uymadiysa kullanici bunu karttan da gormeli - yalnizca
        modelin yanit metnine guvenmek, "degistirildi" deyip yaniltmasina acik.
      */}
      {check && (
        <p
          className={check.ok ? 'toolcard-check' : 'toolcard-check toolcard-check-warn'}
          role="status"
        >
          <span aria-hidden="true">{check.ok ? '✓ ' : '⚠ '}</span>
          {check.ok
            ? t('tool.image.checkOk')
            : t('tool.image.checkFailed', { problems: check.problems })}
          {check.attempts > 1 && ` ${t('tool.image.retried')}`}
        </p>
      )}
      {source && <p className="toolcard-source">{t('tool.image.source', { name: source })}</p>}
      {prompt && <div className="toolcard-query">{prompt}</div>}
      {output && files.length === 0 && <pre className="toolcard-output">{output}</pre>}
      <OutputFiles files={files} preview={false} />
    </section>
  )
}

/**
 * Sunucudaki `convert_to_word` araci: PDF -> duzeni korunmus Word. Kod yok;
 * kaynak dosya adi, kisa sonuc ve uretilen dosyanin indirme baglantisi.
 */
function ConvertCard({ data }: { data: ToolCardData }) {
  const { t } = useTranslation()
  const source = asText(data.source)
  const output = asText(data.output)
  const outputFiles = asOutputFiles(data.output_attachments)
  return (
    <section className="toolcard" aria-label={t('tool.convert.label')}>
      <header>
        <span className="toolcard-kind">{t('tool.convert.label')}</span>
      </header>
      {source && <div className="toolcard-query">{source}</div>}
      {output && <pre className="toolcard-output">{output}</pre>}
      <OutputFiles files={outputFiles} />
    </section>
  )
}

/**
 * Arac ciktisi dosyalari.
 *
 * GORSEL olanlar ONIZLEMELI: matplotlib ile uretilen bir grafik (`savefig`)
 * kod kartinda yalnizca indirme baglantisi olarak gorunuyordu - kullanici
 * grafigi gormek icin dosyayi indirmek zorundaydi (canli geri bildirim).
 * Tiklayinca ayni galeri modali aciliyor; digerleri (xlsx/docx/pdf/csv)
 * eskisi gibi indirme baglantisi.
 */
function OutputFiles({
  files,
  preview = true,
}: {
  files: ToolCardOutputFile[]
  /** `false`: cagiran onizlemeyi KENDI ciziyor (ImageCard) - iki kez gosterme. */
  preview?: boolean
}) {
  const { t } = useTranslation()
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const gallery = useGallery()
  const images = useMemo(
    () => (preview ? files.filter((file) => (file.mime ?? '').startsWith('image/')) : []),
    [files, preview],
  )
  const previews = useMemo<PreviewItem[]>(
    () =>
      images.map((file) => ({
        kind: 'image',
        filename: file.filename,
        mime: file.mime ?? 'image/png',
        byteSize: file.byte_size ?? 0,
        previewUrl: file.url,
        downloadUrl: file.url,
        width: file.width ?? null,
        height: file.height ?? null,
      })),
    [images],
  )
  if (files.length === 0) return null
  return (
    <>
      {images.map((file, index) => (
        <button
          key={file.id}
          type="button"
          className="toolcard-image"
          onClick={() => {
            if (!gallery?.openById(file.id)) setOpenIndex(index)
          }}
          aria-label={t('attach.preview.label', { name: file.filename })}
        >
          <img
            src={file.url}
            alt={file.filename}
            width={file.width ?? undefined}
            height={file.height ?? undefined}
            loading="lazy"
          />
        </button>
      ))}
      {openIndex !== null && (
        <AttachmentModal
          items={previews}
          startIndex={openIndex}
          onClose={() => setOpenIndex(null)}
        />
      )}
      <ul className="toolcard-outputs">
        {files.map((file) => (
          <li key={file.id}>
            <a className="toolcard-output-file" href={file.url} download={file.filename}>
              <DownloadIcon size={16} />
              {file.filename}
            </a>
          </li>
        ))}
      </ul>
    </>
  )
}

function CodeCard({ data }: { data: ToolCardData }) {
  const { t } = useTranslation()
  const code = asText(data.code)
  const output = asText(data.output)
  const outputFiles = asOutputFiles(data.output_attachments)
  const patch = asPatch(data.patch)
  return (
    <section className="toolcard" aria-label={t('tool.code.label')}>
      <header>
        <span className="toolcard-kind">
          Python
          {patch && (
            <span className={patch.failed ? 'toolcard-patch toolcard-patch-failed' : 'toolcard-patch'}>
              {patch.failed
                ? t('tool.code.patchFailed')
                : patch.rerun
                  ? t('tool.code.rerun')
                  : t('tool.code.patched', { lines: patch.lines })}
            </span>
          )}
        </span>
        <span className="toolcard-actions">
          {code && (
            <button type="button" className="copy" onClick={() => downloadScript(code)}>
              <DownloadIcon />
              {t('tool.code.download')}
            </button>
          )}
          <CopyButton text={code} label={t('tool.code.copy')} />
        </span>
      </header>
      <pre className="toolcard-code">{code}</pre>
      {output && (
        <>
          <div className="toolcard-label-row">
            <span className="toolcard-label">{t('tool.code.output')}</span>
            <CopyButton text={output} iconOnly />
          </div>
          <pre className="toolcard-output">{output}</pre>
        </>
      )}
      <OutputFiles files={outputFiles} />
    </section>
  )
}

/**
 * `edit_python` yamasinin ozeti (bkz. `engines/reason.py::_edit_python_tool`):
 * model kodu bastan yazmadi, yalnizca degisen satirlari verdi.
 */
function asPatch(
  value: unknown,
): { lines: number; failed: boolean; rerun: boolean } | null {
  if (!value || typeof value !== 'object') return null
  const patch = value as { changed_lines?: unknown; failed?: unknown; rerun?: unknown }
  return {
    lines: typeof patch.changed_lines === 'number' ? patch.changed_lines : 0,
    failed: patch.failed === true,
    rerun: patch.rerun === true,
  }
}

/** Kartin kodunu `betik.py` olarak indirir (tarayicida; sunucuya gitmez). */
function downloadScript(code: string) {
  const url = URL.createObjectURL(new Blob([code], { type: 'text/x-python' }))
  const link = document.createElement('a')
  link.href = url
  link.download = 'betik.py'
  link.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

interface ToolCardOutputFile {
  id: string
  filename: string
  url: string
  mime?: string
  byte_size?: number
  width?: number | null
  height?: number | null
}

function asOutputFiles(value: unknown): ToolCardOutputFile[] {
  if (!Array.isArray(value)) return []
  return value.filter(
    (item): item is ToolCardOutputFile =>
      !!item &&
      typeof item === 'object' &&
      typeof (item as ToolCardOutputFile).id === 'string' &&
      typeof (item as ToolCardOutputFile).filename === 'string' &&
      typeof (item as ToolCardOutputFile).url === 'string',
  )
}

interface ImageCheck {
  ok: boolean
  problems: string
  attempts: number
}

function asImageCheck(value: unknown): ImageCheck | null {
  if (!value || typeof value !== 'object') return null
  const raw = value as Record<string, unknown>
  if (typeof raw.ok !== 'boolean') return null
  return {
    ok: raw.ok,
    problems: typeof raw.problems === 'string' ? raw.problems : '',
    attempts: typeof raw.attempts === 'number' ? raw.attempts : 1,
  }
}

function SearchCard({ data }: { data: ToolCardData }) {
  const { t } = useTranslation()
  const query = asText(data.query)
  const output = asText(data.output)
  return (
    <section className="toolcard" aria-label={t('tool.search.label')}>
      <header>
        <span className="toolcard-kind">{t('tool.search.label')}</span>
        {output && <CopyButton text={output} iconOnly />}
      </header>
      <div className="toolcard-query">{query}</div>
      {/*
        Arama sonuclari GUVENILMEYEN veri: modelin prompt'una enjekte
        ediliyor ve kotu niyetli bir sayfa modele arac cagirmasini
        soyleyebilir. Burada da duz metin olarak gosteriliyor - markdown
        hattina girmesi, sonucun link ve bicimlendirme uretmesine izin
        vermek olurdu.
      */}
      {output && <pre className="toolcard-output">{output}</pre>}
    </section>
  )
}

/**
 * Yerel ajan araci (`local_*`): modelin kullanicinin BILGISAYARINDA yaptigi
 * islem (dosya, kabuk, SQL...).
 *
 * Hangi aracin, hangi hedefle calistigi ACIKCA gorunuyor: kullanici modelin
 * makinesinde ne yaptigini her zaman gorebilmeli. Cikti GUVENILMEYEN (bir
 * dosyanin icerigi, bir komutun stdout'u enjeksiyon tasiyabilir), bu yuzden
 * arama/kod kartlariyla ayni sekilde DUZ metin olarak - markdown hattina
 * girmeden - gosteriliyor.
 */
function LocalCard({ data }: { data: ToolCardData }) {
  const { t } = useTranslation()
  const tool = asText(data.tool)
  const summary = asText(data.summary) || tool
  const output = asText(data.output)
  return (
    <section className="toolcard" aria-label={t('tool.local.label')}>
      <header>
        <span className="toolcard-kind">{t('tool.local.label')}</span>
        {output && <CopyButton text={output} iconOnly />}
      </header>
      <div className="toolcard-query">{summary}</div>
      {output && <pre className="toolcard-output">{output}</pre>}
    </section>
  )
}

/**
 * `fetch_url`: modelin GERCEKTEN actigi sayfa.
 *
 * Adresin gorunur olmasi bir dogruluk ozelligi: model daha once acamadigi
 * bir sayfanin icerigini uyduruyordu ve kullanicinin bunu dogrulamasinin
 * yolu yoktu. Kart, hangi adresin okundugunu ve okunan METNI gosteriyor.
 */
function FetchCard({ data }: { data: ToolCardData }) {
  const { t } = useTranslation()
  const url = asText(data.url)
  const output = asText(data.output)
  return (
    <section className="toolcard" aria-label={t('tool.fetch.label')}>
      <header>
        <span className="toolcard-kind">{t('tool.fetch.label')}</span>
        {output && <CopyButton text={output} iconOnly />}
      </header>
      {/* Baglanti YOK, duz metin: karta tiklanabilir bir adres koymak, sayfa
          icerigine (guvenilmeyen veri) gore yonlendirme uretmek olurdu. */}
      <div className="toolcard-query">{url}</div>
      {/* Arama karti gibi: sayfa metni GUVENILMEYEN veri, markdown hattina girmiyor. */}
      {output && <pre className="toolcard-output">{output}</pre>}
    </section>
  )
}

/**
 * `remember`: modelin kalici hafizaya yazdigi bilgi.
 *
 * Kart SEFFAFLIK icin var - kullanici hakkinda ne saklandigi sessizce
 * olmamali. Ayarlardaki hafiza panelinden silinebiliyor.
 */
function MemoryCard({ data }: { data: ToolCardData }) {
  const { t } = useTranslation()
  const output = asText(data.output)
  const failed = output.startsWith('[HATA]')
  // Tek cagrida birden fazla bilgi kaydedilebiliyor; hepsi TEK TEK gorunmeli -
  // kullanici hakkinda ne saklandigi okunabilir olmali.
  const lines = (value: unknown) =>
    asText(value)
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
  const facts = lines(data.fact)
  const forgotten = lines(data.forgot)
  return (
    <section className="toolcard" aria-label={t('tool.memory.label')}>
      <header>
        <span className="toolcard-kind">{t('tool.memory.label')}</span>
      </header>
      {facts.map((line) => (
        <div key={line} className="toolcard-query">
          {line}
        </div>
      ))}
      {/* Kaldirilan maddeler de gorunuyor: model eskiyen bir bilgiyi
          (ornegin "artik Python kullanmiyorum") sessizce silmemeli. */}
      {forgotten.length > 0 && (
        <>
          <span className="toolcard-kind">{t('tool.memory.forgot')}</span>
          {forgotten.map((line) => (
            <div key={line} className="toolcard-query memory-forgot">
              {line}
            </div>
          ))}
        </>
      )}
      {failed && <pre className="toolcard-output">{output}</pre>}
      {!failed && <p className="field-hint">{t('tool.memory.hint')}</p>}
    </section>
  )
}

/**
 * Model hafizaya BAKTI (`recall_memory`). Hafiza artik her mesaja eklenmiyor;
 * kullanici modelin ne zaman baktigini gorebilmeli. Okunan maddeler kapali
 * bir ayrintida - her yanitin altinda kisisel bilgileri acik acik dokmek
 * gereksiz.
 */
function RecallCard({ data }: { data: ToolCardData }) {
  const { t } = useTranslation()
  const output = asText(data.output)
  const items = output
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.startsWith('- '))
    .map((line) => line.slice(2))
  return (
    <section className="toolcard" aria-label={t('tool.recall.label')}>
      <header>
        <span className="toolcard-kind">{t('tool.recall.label')}</span>
      </header>
      {items.length > 0 ? (
        <details>
          <summary className="field-hint">{t('tool.recall.count', { count: items.length })}</summary>
          {items.map((line) => (
            <div key={line} className="toolcard-query">
              {line}
            </div>
          ))}
        </details>
      ) : (
        <p className="field-hint">{t('tool.recall.empty')}</p>
      )}
    </section>
  )
}

function UnknownCard({ kind }: { kind: string }) {
  const { t } = useTranslation()
  // Sunucu yeni bir kart turu eklerse arayuz cokmemeli; bilinmeyen tur
  // gorunur ama zararsiz.
  return (
    <section className="toolcard toolcard-unknown">
      <header>
        <span className="toolcard-kind">{t('tool.unknown', { kind })}</span>
      </header>
    </section>
  )
}

function asText(value: unknown): string {
  if (typeof value === 'string') return value
  if (value === null || value === undefined) return ''
  return String(value)
}
